import { MoreVertical, History, Download, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import type { NoteExportFormat } from '../types';

interface NoteOptionsMenuProps {
  onOpenHistory: () => void;
  onDelete: () => void;
  onExport: (format: NoteExportFormat) => void;
}

/**
 * Only the three backend-supported actions ship here — Pin, Convertir en tarea,
 * Duplicar, Mover a carpeta, Compartir and Exportar a PDF are hidden entirely
 * (confirmed scope decision, no backend endpoints exist for them).
 */
export function NoteOptionsMenu({ onOpenHistory, onDelete, onExport }: NoteOptionsMenuProps) {
  const { t } = useTranslation('notes');

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('optionsMenu.label')}
              data-testid="notes-editor-options-menu"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>{t('optionsMenu.label')}</TooltipContent>
      </Tooltip>

      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onOpenHistory} data-testid="notes-editor-history-button">
          <History className="h-4 w-4 mr-2" />
          {t('optionsMenu.viewHistory')}
        </DropdownMenuItem>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Download className="h-4 w-4 mr-2" />
            {t('optionsMenu.export')}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem
              onClick={() => onExport('plaintext')}
              data-testid="notes-editor-export-txt-button"
            >
              {t('optionsMenu.exportTxt')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onExport('markdown')}
              data-testid="notes-editor-export-md-button"
            >
              {t('optionsMenu.exportMd')}
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={onDelete}
          className="text-destructive focus:text-destructive"
          data-testid="notes-editor-delete-button"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          {t('optionsMenu.delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
